import React from 'react';
import { Shield, Lock, Eye, Users, Phone, Mail, Calendar, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const HIPAANoticePage: React.FC = () => {
  const effectiveDate = 'December 15, 2024';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-red-100 rounded-full">
              <Shield className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Notice of Privacy Practices</h1>
          <p className="text-lg text-gray-600">
            This notice describes how medical information about you may be used and disclosed
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Effective Date: {effectiveDate}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Lock className="h-6 w-6 text-red-600 mr-3" />
                Your Rights
              </h2>
              <p className="text-gray-700 mb-4">
                When it comes to your health information, you have certain rights. This section explains your rights and some of our responsibilities to help you.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Get a copy of your medical record</strong> - You can ask to see or get an electronic or paper copy of your medical record and other health information we have about you.</li>
                <li><strong>Correct your medical record</strong> - You can ask us to correct health information about you that you think is incorrect or incomplete.</li>
                <li><strong>Request confidential communications</strong> - You can ask us to contact you in a specific way (for example, home or office phone) or to send mail to a different address.</li>
                <li><strong>Ask us to limit what we use or share</strong> - You can ask us not to use or share certain health information for treatment, payment, or our operations.</li>
                <li><strong>Get a list of those with whom we've shared information</strong> - You can ask for a list (accounting) of the times we've shared your health information for six years prior to the date you ask.</li>
                <li><strong>Get a copy of this privacy notice</strong> - You can ask for a paper copy of this notice at any time.</li>
                <li><strong>Choose someone to act for you</strong> - If you have given someone medical power of attorney or if someone is your legal guardian, that person can exercise your rights and make choices about your health information.</li>
                <li><strong>File a complaint if you feel your rights are violated</strong> - You can complain if you feel we have violated your rights by contacting us.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Eye className="h-6 w-6 text-red-600 mr-3" />
                Your Choices
              </h2>
              <p className="text-gray-700 mb-4">
                For certain health information, you can tell us your choices about what we share. If you have a clear preference for how we share your information in the situations described below, talk to us.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Share information with your family, close friends, or others involved in your care</strong></li>
                <li><strong>Share information in a disaster relief situation</strong></li>
                <li><strong>Include your information in a hospital directory</strong></li>
                <li><strong>Contact you for fundraising efforts</strong></li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="h-6 w-6 text-red-600 mr-3" />
                Our Uses and Disclosures
              </h2>
              <p className="text-gray-700 mb-4">
                We typically use or share your health information in the following ways:
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">Treat you</h3>
              <p className="text-gray-700 mb-4">
                We can use your health information and share it with other professionals who are treating you.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">Run our organization</h3>
              <p className="text-gray-700 mb-4">
                We can use and share your health information to run our practice, improve your care, and contact you when necessary.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">Bill for your services</h3>
              <p className="text-gray-700 mb-4">
                We can use and share your health information to bill and get payment from health plans or other entities.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">How else can we use or share your health information?</h2>
              <p className="text-gray-700 mb-4">
                We are allowed or required to share your information in other ways – usually in ways that contribute to the public good, such as public health and research. We have to meet many conditions in the law before we can share your information for these purposes.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Help with public health and safety issues</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Preventing disease</li>
                    <li>• Helping with product recalls</li>
                    <li>• Reporting adverse reactions to medications</li>
                    <li>• Reporting suspected abuse, neglect, or domestic violence</li>
                    <li>• Preventing or reducing a serious threat to anyone's health or safety</li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Do research</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• We can use or share your information for health research</li>
                    <li>• Research must be approved by an institutional review board</li>
                    <li>• Your information will be protected during research</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Comply with the law</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• We will share information about you if state or federal laws require it</li>
                    <li>• Including with the Department of Health and Human Services</li>
                    <li>• If it's needed for legal proceedings</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Address workers' compensation, law enforcement, and other government requests</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• For workers' compensation claims</li>
                    <li>• For law enforcement purposes</li>
                    <li>• For health oversight activities</li>
                    <li>• For special government functions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Responsibilities</h2>
              <p className="text-gray-700 mb-4">
                We are required by law to maintain the privacy and security of your protected health information.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>We will let you know promptly if a breach occurs that may have compromised the privacy or security of your information.</li>
                <li>We must follow the duties and privacy practices described in this notice and give you a copy of it.</li>
                <li>We will not use or share your information other than as described here unless you tell us we can in writing.</li>
                <li>If you tell us we can, you may change your mind at any time. Let us know in writing if you change your mind.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to the Terms of This Notice</h2>
              <p className="text-gray-700">
                We can change the terms of this notice, and the changes will apply to all information we have about you. The new notice will be available upon request, in our office, and on our website.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this notice or our privacy practices, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-gray-700">
                  <strong>Privacy Officer:</strong> privacy@therapistdirect.com
                </p>
                <p className="text-gray-700">
                  <strong>Phone:</strong> 1-800-WELLNESS
                </p>
                <p className="text-gray-700">
                  <strong>Address:</strong> 123 Wellness St, Suite 400, Mental Health City, MH 12345
                </p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">File a Complaint</h2>
              <p className="text-gray-700 mb-4">
                If you feel your rights have been violated, you can file a complaint with us or with the U.S. Department of Health and Human Services Office for Civil Rights.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800">How to File a Complaint</h4>
                    <ul className="text-sm text-red-700 mt-2 space-y-1">
                      <li>• Contact us directly at privacy@therapistdirect.com</li>
                      <li>• Call us at 1-800-WELLNESS</li>
                      <li>• File a complaint with HHS Office for Civil Rights</li>
                      <li>• Visit <a href="https://www.hhs.gov/ocr" className="underline" target="_blank" rel="noopener noreferrer">www.hhs.gov/ocr</a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <p className="text-sm text-gray-500">
                  © {new Date().getFullYear()} TherapistDirect. All rights reserved.
                </p>
                <div className="flex space-x-6 text-sm">
                  <Link to="/privacy" className="text-red-600 hover:text-red-800">
                    Privacy Policy
                  </Link>
                  <Link to="/terms" className="text-red-600 hover:text-red-800">
                    Terms of Service
                  </Link>
                  <Link to="/accessibility" className="text-red-600 hover:text-red-800">
                    Accessibility
                  </Link>
                  <Link to="/" className="text-red-600 hover:text-red-800">
                    Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HIPAANoticePage;
